import { useState } from "react";


/**
 * custom hook to control a boolean value
 * used in cases where some control can be on or off
 *
 * returns of tuple of type ToggleHook
 */
export type ToggleHook = [
    boolean,
    {
        on(): void,
        off(): void,
        toggle(): void
    }
];


export function useToggle( initialValue: boolean = false ): ToggleHook {
    const [ state, setState ] = useState( initialValue );

    const handlers = {
            on: () => { setState( true ); },
            off: () => { setState( false ); },
            toggle: () => { setState( ! state ); }
        };

    return [ state, handlers ];
}

/**
 * wrapper hook around the useToggle hook to provide a better to understand naming scheme
 * for the many cases where something should only be visible in certain cases
 *
 * @see useToggle
 */
export type VisibilityHook = [
    boolean,
    {
        show(): void,
        hide(): void,
        toggleVisibility(): void,
        setVisibility( isVisible: boolean ): void
    }
];

export function useVisibility( initialValue: boolean = false ): VisibilityHook {
    const [ isVisible, { on, off, toggle }] = useToggle( initialValue );

    const handlers = {
            show: () => on(),
            hide: () => off(),
            toggleVisibility: () => toggle(),
            setVisibility: ( isVisible: boolean ) => isVisible ? on() : off()
        };

    return [ isVisible, handlers ];
}

/**
 * keeps track of state in an object of visibility booleans
 * given keyId must be unique!
 */
export type VisibilityObject = { [ key: number ]: boolean }

export type VisibilityObjectHook = [
    { [ key: number ]: boolean },
    {
        toggleVisibility: ( idKey: number ) => void
        addStateItem: ( idKey: number ) => void
        removeStateItem: ( idKey: number ) => void
    }
]

export function useVisibilityObject( initialState: VisibilityObject ): VisibilityObjectHook {
    const [ state, setState ] = useState< VisibilityObject >( initialState );


    const handlers = {
        toggleVisibility: ( idKey : number) => {
                setState({
                    ...state,
                    [ idKey ]: ! state[ idKey ]
                });
            },
        removeStateItem: ( idKey: number ) => {

                const remainingState = Object
                    .keys( state )
                    .filter( _key => parseInt( _key ) !== idKey )
                    .reduce( ( _acc, _key ) => ({
                        ..._acc,
                        [ _key ]: state[ parseInt( _key ) ]
                    }), {} );

                setState( remainingState );
        },
        addStateItem: ( idKey: number ) => {
                setState({
                    ...state,
                    [ idKey ]: true
                });
            }
        };

        return [ state, handlers ];
}

